import type { AppTextareaEvent, NativeSelectOption } from '@shared/ui/fields'
import type { LessonsBulkUpdatePayload } from './interfaces'
import type { LessonStatusReasons } from './lesson-status'

import { ReasonCategory, Status as BookingStatus } from '@brighterly/lib-core-types/booking/enums'

import { useListsStore } from '@shared/model'

import { useLessonsStore } from './store'
import { useLessonsBulkUpdateApi } from '../../api/lessons/bulk-update.hook'
import { useLessonsRefresh } from './lessons-refresh.hook'
import { LESSON_NO_TUTOR_ID } from './lesson-active-tutor-select.hook'
import { isReservedLesson } from './lessons'
import {
  buildLessonStatusOptions,
  buildReasonCategoryOptions,
  buildReasonOptions,
  getFirstReason,
  getLessonStatusChoices,
  getTutorReassignReasons,
  isLessonReasonStatus,
} from './lesson-status'

interface ModalLessonsBulkEditReturn {
  refTitle: ComputedRef<string>
  refStatusOptions: ComputedRef<NativeSelectOption[]>
  refStatus: Ref<BookingStatus>
  refIsReasonShown: ComputedRef<boolean>
  refCategoryOptions: ComputedRef<NativeSelectOption[]>
  refCategory: Ref<string | null>
  refReasonOptions: ComputedRef<NativeSelectOption[]>
  refReason: Ref<string | null>
  refTutorId: Ref<number>
  refIsReassignStep: Ref<boolean>
  refReassignOptions: ComputedRef<NativeSelectOption[]>
  refReassignReason: Ref<string | null>
  refReassignComment: Ref<string>
  refIsReassignInvalid: Ref<boolean>
  refSubmitText: ComputedRef<string>
  refIsSaving: Ref<boolean>
  setStatus: (option: NativeSelectOption) => void
  setCategory: (option: NativeSelectOption) => void
  setReason: (option: NativeSelectOption) => void
  setReassignReason: (option: NativeSelectOption) => void
  setReassignComment: (event: AppTextareaEvent) => void
  closeReassignStep: () => void
  close: () => void
  submit: () => Promise<void>
}

/**
 * One status and one tutor for every ticked lesson, as the legacy bulk edit did: both are always sent. Replacing a
 * lesson's tutor asks for the reason first; the lessons that fail are reported one by one. The modal stays open
 * under its loader until the table is reread, and closes at once on a refusal.
 */
export const useModalLessonsBulkEdit = (): ModalLessonsBulkEditReturn => {
  const route = useRoute()
  const store = useLessonsStore()
  const listsStore = useListsStore()
  const { refreshLessons } = useLessonsRefresh()

  const selected = store.getLessons.filter(lesson => store.getSelectedIds.includes(lesson.id))
  const isReservedSelection = selected.length > 0 && selected.every(lesson => isReservedLesson(lesson.status))
  const firstTutorId = selected.find(lesson => lesson.tutor_id)?.tutor_id ?? LESSON_NO_TUTOR_ID

  const reasons = computed<LessonStatusReasons>(() =>
    (listsStore.getLists?.booking.change_status_reasons ?? {}) as LessonStatusReasons)

  const refStatus = ref<BookingStatus>(isReservedSelection ? BookingStatus.ReservedPlanned : BookingStatus.Draft)
  const refCategory = ref<string | null>(ReasonCategory.Admin)
  const refReason = ref<string | null>(getFirstReason(reasons.value, ReasonCategory.Admin))
  const refTutorId = ref<number>(firstTutorId)
  const refIsReassignStep = ref<boolean>(false)
  const refReassignReason = ref<string | null>(null)
  const refReassignComment = ref<string>('')
  const refIsReassignInvalid = ref<boolean>(false)
  const refIsSaving = ref<boolean>(false)

  /** Only replacing an assigned tutor needs a reason; giving lessons their first tutor does not. */
  const refNeedsReason = computed<boolean>(() =>
    selected.some(lesson => lesson.tutor_id && lesson.tutor_id !== refTutorId.value))

  const refTitle = computed<string>(() => (refIsReassignStep.value ? 'Select reason' : 'Bulk edit'))

  const refStatusOptions = computed<NativeSelectOption[]>(() => {
    const labels = listsStore.getLists?.booking.status ?? {}
    const choices = getLessonStatusChoices(labels, isReservedSelection ? BookingStatus.ReservedPlanned : BookingStatus.Draft)

    return buildLessonStatusOptions(labels, choices, refStatus.value)
  })

  const refIsReasonShown = computed<boolean>(() => isLessonReasonStatus(refStatus.value))

  const refCategoryOptions = computed<NativeSelectOption[]>(() =>
    buildReasonCategoryOptions(reasons.value, listsStore.getLists?.booking.reason_category ?? {}, refCategory.value))

  const refReasonOptions = computed<NativeSelectOption[]>(() =>
    buildReasonOptions(reasons.value, refCategory.value, refReason.value))

  const refReassignOptions = computed<NativeSelectOption[]>(() => [
    { id: null, value: null, text: '--- Not selected ---', selected: !refReassignReason.value, disabled: true },
    ...Object.entries(getTutorReassignReasons(listsStore.getLists?.tutor.reassign_reason)).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === refReassignReason.value,
    })),
  ])

  const refSubmitText = computed<string>(() => (refNeedsReason.value && !refIsReassignStep.value ? 'Continue' : 'Save'))

  const setStatus = (option: NativeSelectOption): void => {
    refStatus.value = option.value as BookingStatus
  }

  const setCategory = (option: NativeSelectOption): void => {
    refCategory.value = option.value ? String(option.value) : null
    refReason.value = getFirstReason(reasons.value, refCategory.value)
  }

  const setReason = (option: NativeSelectOption): void => {
    refReason.value = option.value ? String(option.value) : null
  }

  const setReassignReason = (option: NativeSelectOption): void => {
    refReassignReason.value = option.value ? String(option.value) : null
    refIsReassignInvalid.value = false
  }

  const setReassignComment = (event: AppTextareaEvent): void => {
    refReassignComment.value = event.value
  }

  const closeReassignStep = (): void => {
    refIsReassignStep.value = false
  }

  const close = (): void => {
    if (refIsSaving.value) return
    store.closeActionModal()
  }

  const buildPayload = (): LessonsBulkUpdatePayload => {
    const payload: LessonsBulkUpdatePayload = {
      ids: selected.map(lesson => lesson.id),
      status: refStatus.value,
      tutor_id: refTutorId.value || null,
    }

    if (isLessonReasonStatus(refStatus.value)) {
      payload.reason = refReason.value
      payload.reason_category = refCategory.value
    }

    if (refNeedsReason.value) {
      payload.tutor_reassign_reason = refReassignReason.value
      payload.tutor_reassign_comment = refReassignComment.value.trim() || null
    }

    return payload
  }

  const submit = async (): Promise<void> => {
    if (refIsSaving.value) return

    if (refNeedsReason.value && !refIsReassignStep.value) {
      refIsReassignStep.value = true
      return
    }

    if (refNeedsReason.value && !refReassignReason.value) {
      refIsReassignInvalid.value = true
      return
    }

    refIsSaving.value = true

    const result = await useLessonsBulkUpdateApi({
      disciplineId: route.params.id as string,
      payload: buildPayload(),
    })

    // A refused request changed nothing: the error is shown and the modal goes away.
    if (!result) {
      refIsSaving.value = false
      store.closeActionModal()
      return
    }

    // The modal keeps its loader until the table shows the change.
    await refreshLessons()

    refIsSaving.value = false
    store.clearSelected()
    store.closeActionModal()
  }

  return {
    refTitle,
    refStatusOptions,
    refStatus,
    refIsReasonShown,
    refCategoryOptions,
    refCategory,
    refReasonOptions,
    refReason,
    refTutorId,
    refIsReassignStep,
    refReassignOptions,
    refReassignReason,
    refReassignComment,
    refIsReassignInvalid,
    refSubmitText,
    refIsSaving,
    setStatus,
    setCategory,
    setReason,
    setReassignReason,
    setReassignComment,
    closeReassignStep,
    close,
    submit,
  }
}
